<template>
  <transition name="slide-from-bottom">
    <div
      v-if="isOpen && !currentlyOpenModal"
      class="card card--dark reverse-colors padding-full"
      :class="{
        'card--clickable': !disableOffersPageLink
      }"
      @click="goToOffersPage"
    >
      <div class="flex gap align-center">
        <template v-if="loyalty.isMembershipTierStatus">
          <div class="dist-y-half">
            <h2 class="title title--4 half-spacing">{{ loyalty.tier.title }}</h2>
            <p class="half-spacing smaller">{{ loyalty.tier.description }}</p>
            <p class="dark"><b>{{ rewardCount }} Reward<template v-if="rewardCount !== 1">s</template> Available</b></p>
          </div>

          <div
            v-if="loyalty.tier.current && loyalty.tier.current.badgeDefaultUrl"
            class="no-shrink"
          >
            <img
              class="image image--logo-smaller"
              :alt="loyalty.tier.current.name"
              :src="loyalty.tier.current.badgeDefaultUrl"
            >
          </div>
        </template>

        <template v-else>
          <div class="dist-y-half">
            <template v-if="rewardWasJustEarned">
              <h2 class="title title--4 half-spacing">You earned a reward!</h2>
              <p class="smaller">You earned {{ numberOfPointsJustEarned }} {{ loyalty.currency.pluralName }} and <b class="dark">{{ rewardTitle }}</b> on your next order.</p>
            </template>

            <template v-else>
              <h2
                v-if="numberOfPointsJustEarned"
                class="title title--4"
              >
                You earned {{ numberOfPointsJustEarned }} {{ loyalty.currency.pluralName }}!
              </h2>
              <h2
                v-else
                class="title title--4 half-spacing"
              >
                Loyalty {{ loyalty.currency.pluralNameCapitalCase }}
              </h2>

              <template v-if="showPersonalLoyaltyInfo">
                <p
                  v-if="loyalty.pointsUntilNextReward"
                  class="half-spacing smaller"
                >
                  {{ loyalty.pointsUntilNextReward }} {{ loyalty.currency.pluralName }} until your next reward.
                </p>
                <p
                  v-else
                  class="half-spacing smaller"
                >
                  You've unlocked every reward!
                </p>
                <p class="dark smaller"><b>{{ rewardCount }} Reward<template v-if="rewardCount !== 1">s</template> Available</b></p>
              </template>
            </template>
          </div>

          <loyalty-progress-ring
            v-if="showPersonalLoyaltyInfo"
            class="spacing-left"
          />
        </template>

        <button
          type="button"
          class="card__close icon icon--smedium icon--alt-link-reverse dark fal fa-times"
          title="Close loyalty summary"
          @click.stop="close"
        />
      </div>
    </div>
  </transition>
</template>



<script>
  import events from '@/services/events';
  import { mapGetters } from 'vuex';



  export default {
    name: 'LoyaltyPopupCard',

    data() {
      return {
        disableOffersPageLink: false,
        hasAutoOpened: false,
        isOpen: false,
        numberOfPointsJustEarned: 0,
        rewardTitle: null,
        rewardWasJustEarned: false,
        showPersonalLoyaltyInfo: true
      };
    },

    computed: {
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('modals', ['currentlyOpenModal']),
      ...mapGetters('offers', ['loyalty', 'rewardCount']),
      ...mapGetters('session', ['currentUser', 'orderDetails'])
    },

    created() {
      this.createdHandler();
    },

    unmounted() {
      this.destroyedHandler();
    },

    methods: {
      autoOpen() {
        if (this.hasAutoOpened) {
          return true;
        }

        this.open();
      },

      close() {
        this.isOpen = false;
        this.numberOfPointsJustEarned = 0;
        this.rewardTitle = null;
        this.rewardWasJustEarned = false;
        events.off('route', this.close);
      },

      createdHandler() {
        events.on('loyalty-popup-card:open', this.openHandler);
        events.on('modals:open', this.close);
        events.on('signed-in', this.signInHandler);
        events.on('signed-out', this.close);
      },

      destroyedHandler() {
        events.off('loyalty-popup-card:open', this.openHandler);
        events.off('modals:open', this.close);
        events.off('signed-in', this.signInHandler);
        events.off('signed-out', this.close);
      },

      goToOffersPage() {
        if (this.disableOffersPageLink) {
          return;
        }

        this.$router.push({
          name: 'loyalty',
          params: {
            merchantId: this.$route.params.merchantId
          }
        });
      },

      async open() {
        this.isOpen = true;
        this.hasAutoOpened = true;
        events.on('route', this.close);
      },

      async openHandler({ disableOffersPageLink, receipt, refreshData, autoOpen } = {}) {
        this.disableOffersPageLink = disableOffersPageLink || !this.currentUser.accountId || false;

        if (refreshData) {
          await this.$store.dispatch('offers/getOffers', {
            silentError: true
          });
        }

        if (receipt) {
          this.disableOffersPageLink = disableOffersPageLink || !receipt.isLinkedToCurrentAuthenticatedUser;
          this.showPersonalLoyaltyInfo = receipt.isLinkedToCurrentAuthenticatedUser;

          if (receipt.rewardsSummary) {
            const { earnedNewReward, offerTitle, pointsJustEarned } = receipt.rewardsSummary;

            this.rewardWasJustEarned = earnedNewReward;

            this.rewardTitle = offerTitle;

            this.numberOfPointsJustEarned = pointsJustEarned;
          }
        }

        if ((!this.currentUser.accountId || !this.loyalty.isEnrolled) && !receipt?.loyaltyEngagementSummary) {
          /**
          * don't show the loyalty popup if there is no logged in user
          * or the logged in user is not enrolled in loyalty
          * unless the receipt contains loyalty data
          */
          return;
        }

        if (autoOpen) this.autoOpen();
        else this.open();
      },

      signInHandler() {
        const { loyaltyPopupOnSignInEnabled } = this.merchant.options.loyaltyOptions;
        const isPayAtTable = ['pay', 'table'].includes(this.orderDetails.orderType);

        if (this.loyalty.isEnrolled && loyaltyPopupOnSignInEnabled && !isPayAtTable) {
          this.open();
        }
      }
    }
  };
</script>
